import React from 'react';
import { Link } from 'react-router-dom';
import { motion, useReducedMotion } from 'framer-motion';
import { BadgeCheckIcon, HeartIcon, HomeIcon, TruckIcon, ZapIcon } from 'lucide-react';
import { Rating } from '../ui/Rating';
import { Badge } from '../ui/Badge';
import { cuisineLabel } from '../../data/taxonomy';
import { useAppData } from '../../contexts/AppDataContext';
import { fromPricePerGuest, fromPriceUnit, cheapestMenu, menuQualifier } from '../../utils/pricing';
import { currency, responseTime } from '../../utils/format';
import { useLocale } from '../../contexts/LocaleContext';
import { spring } from '../../utils/motion';
import { cn } from '../../utils/cn';
import type { Chef } from '../../types/chef';
function FulfillmentTags({
  chef
}: {
  chef: Chef;
}) {
  return <>
      {chef.fulfillment.includes('in_home') && <span className="inline-flex items-center gap-1 text-[12px] text-ink-2" data-id="element-277" data-mp="u7f7jxi8undt">
          <HomeIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-278" data-mp="pdufsn78upn2" /> Cooks at yours
        </span>}
      {chef.fulfillment.includes('delivery') && <span className="inline-flex items-center gap-1 text-[12px] text-ink-2" data-id="element-279" data-mp="32hr139uj955q">
          <TruckIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-280" data-mp="32jtt1zf8x8d3" /> Cooks &amp; delivers
        </span>}
    </>;
}
export function SaveButton({
  chefId,
  className
}: {
  chefId: string;
  className?: string;
}) {
  const {
    savedChefIds,
    toggleSaved
  } = useAppData();
  const saved = savedChefIds.includes(chefId);
  const reduce = useReducedMotion();
  return <motion.button type="button" onClick={() => toggleSaved(chefId)} whileTap={reduce ? undefined : {
    scale: 0.9
  }} transition={spring.snappy} aria-pressed={saved} aria-label={saved ? 'Remove from saved chefs' : 'Save this chef'} className={cn('inline-flex h-9 w-9 items-center justify-center rounded-full border border-hairline bg-surface/95 backdrop-blur transition-colors duration-150 ease-out', saved ? 'text-action' : 'text-ink-3 hover:text-ink', className)} data-id="element-281" data-mp="2ivkyxju56say">
      <HeartIcon className={cn('h-4 w-4', saved && 'fill-action')} aria-hidden="true" data-id="element-282" data-mp="1lzsx31fddlb" />
    </motion.button>;
}
export function ChefCard({
  chef
}: {
  chef: Chef;
}) {
  const {
    locale,
    currencyCode,
    t
  } = useLocale();
  const price = fromPricePerGuest(chef);
  const unit = fromPriceUnit(chef);
  const verified = chef.verifications.filter(v => v.status === 'verified').length;
  return <article className="group relative flex h-full flex-col overflow-hidden rounded-xl border border-hairline bg-surface" data-id="element-283" data-mp="kc7pt3ej77t2">
      <div className="relative" data-id="element-284" data-mp="fkzkaigbo6l6">
        <Link to={`/chefs/${chef.slug}`} className="block overflow-hidden" data-id="element-285" data-mp="fjwteaj02459">
          <img src={chef.photo} alt={chef.name} className="aspect-[5/4] w-full object-cover transition-transform duration-300 ease-out group-hover:scale-[1.02]" loading="lazy" data-id="element-286" data-mp="ald543gxog9q" />
        </Link>
        <SaveButton chefId={chef.id} className="absolute right-3 top-3" data-id="element-287" data-mp="1398yozb7hs2a" />
        {chef.bookingMode === 'instant' && <Badge tone="accent" className="absolute left-3 top-3" data-id="element-288" data-mp="1fl1k3l2r4du1">
            <ZapIcon className="h-3 w-3" aria-hidden="true" data-id="element-289" data-mp="1fne0vuwrwi5i" /> Instant book
          </Badge>}
      </div>

      <div className="flex flex-1 flex-col p-4" data-id="element-290" data-mp="1kmqxh7dg53la">
        <div className="flex items-start justify-between gap-3" data-id="element-291" data-mp="1pkisia9e15tp">
          <h3 className="font-display text-lg font-semibold leading-tight tracking-[-0.01em] text-ink" data-id="element-292" data-mp="1uitxa6ktsq52">
            <Link to={`/chefs/${chef.slug}`} className="hover:text-primary" data-id="element-293" data-mp="1yjnmr4dx2d02">
              {chef.name}
            </Link>
          </h3>
          <Rating value={chef.rating} count={chef.reviewCount} data-id="element-294" data-mp="23c73ga7who93" />
        </div>

        <p className="mt-1.5 line-clamp-2 text-[13px] leading-relaxed text-ink-2" data-id="element-295" data-mp="28a8lqsxi5jji">{chef.headline}</p>

        <ul className="mt-3 flex flex-wrap gap-1.5" data-id="element-296" data-mp="2da4nsmmn30wf">
          {chef.cuisineIds.slice(0, 3).map(id => <li key={id} className="rounded-full bg-oat px-2.5 py-1 text-[12px] font-medium text-ink-2" data-id="element-297" data-mp="2i8y2ujhkqzyp">
              {cuisineLabel(id)}
            </li>)}
        </ul>

        <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1" data-id="element-298" data-mp="3a18gf3415sq5">
          <FulfillmentTags chef={chef} data-id="element-299" data-mp="3opcx61jivu6a" />
          {verified >= 3 && <span className="inline-flex items-center gap-1 text-[12px] text-primary" data-id="element-300" data-mp="3orfn6lkodnb0">
              <BadgeCheckIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-301" data-mp="3jr1q1ykm3xrr" /> {verified} checks
            </span>}
        </div>

        <div className="mt-auto flex items-end justify-between gap-3 pt-4" data-id="element-302" data-mp="2r1ksynm1rsp7">
          <p data-id="element-303" data-mp="2r3p67w5u6425">
            <span className="tnum font-display text-xl font-semibold text-ink" data-id="element-304" data-mp="351dnku0p2t2g">{currency(price, locale, currencyCode)}</span>
            <span className="text-[13px] text-ink-2" data-id="element-305" data-mp="30z8upxi2m0gv"> / {unit === 'guest' ? t('common.perGuest') : t('common.perPortion')}</span>
            <span className="mt-0.5 block text-[12px] text-ink-3" data-id="element-306" data-mp="k9ts3evr61bh">{menuQualifier(cheapestMenu(chef))}</span>
          </p>
          <span className="shrink-0 text-[12px] text-ink-3" data-id="element-307" data-mp="gbm7ua22yhvj">replies in {responseTime(chef.responseMinutes)}</span>
        </div>
      </div>
    </article>;
}